import { useEffect, useRef, useState } from 'react'
import type { Attachment } from '@/components/AttachmentPicker'
import { t } from '@/lib/i18n'

/**
 * 稿子的 `.attAudio` 行：▶ / 名字 + 大小 / 进度条 / 🗑。
 *
 * 播放用一个 `new Audio()` 而不是 `<audio controls>`：原生控件在 iOS 上是
 * 一整块系统 UI，高度不可控、颜色不跟深色走，而这里只需要「播 / 停 + 到哪了」。
 *
 * 一次只播一条 —— 两条一起响听不出哪条是哪条。
 *
 * 对元素类型**泛化**（同 ui.tsx 的 `Segments<T extends string>`）：这个组件只用到
 * `Attachment` 那几个字段，不该因此要求调用方把更宽的类型窄回来。相册页传的是
 * 带 `source` / `item_body` 的 GalleryItem，而它的 onDelete 要读 `source` 才知道
 * 打哪条删除路由 —— 不泛化就只能在调用点加一个 `as`，而 `as` 是把类型检查关掉，
 * 正好关在「选错路由会静默不删」那个地方。
 */
export default function AudioList<T extends Attachment>({ items, onDelete }: {
  items: T[]
  onDelete?: (item: T) => void
}) {
  const [playingId, setPlayingId] = useState<number | null>(null)
  const [progress, setProgress] = useState(0)
  const [failed, setFailed] = useState<number | null>(null)
  const el = useRef<HTMLAudioElement | null>(null)

  const stop = () => {
    el.current?.pause()
    el.current = null
    setPlayingId(null)
    setProgress(0)
  }

  // 列表变了（删掉正在播的那条、换了一条作业）就停下，别让声音留在后台
  useEffect(() => {
    if (playingId !== null && !items.some((i) => i.id === playingId)) stop()
  }, [ items, playingId ])

  useEffect(() => stop, [])

  const toggle = (item: Attachment) => {
    if (playingId === item.id) {
      stop()
      return
    }
    stop()
    setFailed(null)

    const audio = new Audio(item.url)
    el.current = audio
    setPlayingId(item.id)
    audio.ontimeupdate = () => {
      if (audio.duration && isFinite(audio.duration)) setProgress(audio.currentTime / audio.duration)
    }
    audio.onended = stop
    // 播不了要**说出来**（编码不支持、签名 URL 过期了）——静默不响会让人以为录坏了
    audio.onerror = () => {
      stop()
      setFailed(item.id)
    }
    audio.play().catch(() => {
      stop()
      setFailed(item.id)
    })
  }

  if (items.length === 0) return null

  return (
    <div className="mt-3">
      <p className="m-0 mb-1.5 px-1 text-[13px] font-bold text-muted">
        {t('homework.audio_section', { count: items.length })}
      </p>
      <div className="bg-sunk rounded-ctl overflow-hidden">
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-3 px-3 py-2.5 border-b border-line last:border-b-0">
            <button
              type="button"
              onClick={() => toggle(item)}
              aria-label={playingId === item.id
                ? t('homework.audio_pause_aria', { name: item.name })
                : t('homework.audio_play_aria', { name: item.name })}
              className="shrink-0 min-w-tap min-h-tap rounded-full bg-tint text-brand text-base font-bold"
            >
              {playingId === item.id ? '⏸' : '▶'}
            </button>

            <span className="flex-1 min-w-0">
              <span className="block text-[13px] font-semibold truncate">{item.name}</span>
              <span className="block text-faint text-[11px] mt-0.5">
                {failed === item.id
                  ? t('homework.audio_failed')
                  : item.size
                    ? t('homework.audio_size', { count: Math.max(1, Math.round(item.size / 1024)) })
                    : ''}
              </span>
              {/* 进度条：只在这一条正在播的时候撑开，不然一排空槽很吵 */}
              {playingId === item.id && (
                <span className="block mt-1.5 h-[3px] rounded-full bg-line overflow-hidden">
                  <span
                    className="block h-full bg-brand transition-[width] duration-200"
                    style={{ width: `${Math.round(progress * 100)}%` }}
                  />
                </span>
              )}
            </span>

            {onDelete && (
              <button
                type="button"
                onClick={() => onDelete(item)}
                aria-label={t('homework.audio_delete_aria', { name: item.name })}
                className="shrink-0 min-w-tap min-h-tap rounded-chip text-muted text-sm active:bg-card active:text-red"
              >
                🗑
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
